import { Box, useTheme } from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { ReactNode } from "react";

/**
 * Page shell for the waste detail pages: a header that stays put and a gray
 * canvas beneath it that scrolls on its own.
 *
 * The header is a flex child at its natural height, so the page itself never
 * scrolls — only the canvas does. That keeps the title, the facility identity
 * and the page actions reachable however far down the cards you are, which is
 * the pattern the tenant waste-streams and manifests pages already use.
 *
 * The offsets subtract only the app bar: the waste routes render through a bare
 * `<Outlet />` with no tab strip, so there is no `tabsOffset` to account for
 * here the way there is on the tabbed pages.
 */
export function WastePageFrame({ header, children }: { header: ReactNode; children: ReactNode }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
        minHeight: 0,
      }}
    >
      <Box sx={{ flexShrink: 0, px: 3, pt: 2, pb: 2 }}>{header}</Box>
      {/* The cards are what scroll, and the gray starts with them — so the
          header above reads as page furniture rather than as another card. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: "canvas",
          px: 3,
          py: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
